import './BannerSliderFromDirectory.scss';

import { b2x } from '@b2x/react/src';
import classnames from 'classnames';
import React from 'react';

import { Button } from './Button';
import { ComponentWrapper } from './ComponentWrapper';
import {
  BannerSliderContentType,
  BannerSliderSlideContentSection,
  ComponentOptionsContentSection,
} from './contentTypes';
import { t } from './i18n/i18n';

export interface BannerSliderFromDirectoryProps extends ComponentOptionsContentSection {
  directoryCode: string;
}

export const BannerSliderFromDirectory = ({ directoryCode, options }: BannerSliderFromDirectoryProps) => {
  const directory = b2x.useDirectory<BannerSliderContentType, BannerSliderSlideContentSection>(directoryCode, {
    populate: { additionalContents: true, content: true },
  });
  const { getPagePath } = b2x.useAppStaticContext();

  return (
    <ComponentWrapper
      classNames="banner-slider-from-directory"
      options={{
        ...options,
        container: undefined,
        margin: {
          bottom: options?.margin?.bottom,
          top: undefined,
        },
        padding: {
          bottom: undefined,
          top: undefined,
        },
      }}
    >
      {directory && (
        <b2x.SwiperContext>
          {({ navigationNextElRef, navigationPrevElRef }) => (
            <b2x.Div className="position-relative">
              <b2x.Swiper
                navigation={{ custom: true }}
                pagination={{ clickable: true }}
                slides={directory.additionalContents?.map((additionalContent) => {
                  const contentSection = additionalContent.body;

                  const alignItems = contentSection.options?.contentAlignment?.vertical as b2x.ResponsiveAlignItems;
                  const justifyContent = contentSection.options?.contentAlignment?.horizontal as b2x.JustifyContent;
                  const textAlign = contentSection.options?.textAlign as b2x.TextAlign;
                  const headingType = contentSection.options?.headingType as string;

                  return (
                    <b2x.Div className="position-relative" key={additionalContent.id}>
                      {contentSection.asset?.type && (
                        <b2x.Div className="asset">
                          <b2x.router.Link
                            aria-label={t('accessibility.advantages')}
                            to={
                              contentSection.content?.cta?.to?.href
                                ? contentSection.content.cta.to.href
                                : contentSection.content?.cta?.to?.code &&
                                  getPagePath(contentSection.content.cta.to.code)
                            }
                          >
                            <b2x.AssetV2 {...contentSection.asset} fluid />
                          </b2x.router.Link>
                        </b2x.Div>
                      )}
                      {(contentSection.content?.title || contentSection.content?.message) && (
                        <b2x.Div
                          alignItems={alignItems}
                          className="content-wrapper"
                          display={'flex'}
                          paddingBottom={{ lg: 5, xs: 3 }}
                          paddingTop={{ lg: 5, xs: 4 }}
                          paddingX={{ lg: 5, xs: 0 }}
                          style={{ pointerEvents: 'none' }}
                        >
                          <b2x.Container>
                            <b2x.Div display={'flex'} justifyContent={{ lg: justifyContent, xs: 'center' }}>
                              <b2x.Div
                                className="content"
                                display={'flex'}
                                flexDirection={'column'}
                                gap={3}
                                paddingX={{ lg: 5, xs: 4 }}
                                style={{ pointerEvents: 'initial' }}
                                textAlign={{ lg: textAlign, xs: 'center' }}
                              >
                                {contentSection.content.title &&
                                  React.createElement(headingType, { class: 'h1 m-0' }, contentSection.content.title)}
                                {contentSection.content.message && (
                                  <b2x.Div className={classnames('h3 m-0')}>
                                    {b2x.formatHtml(contentSection.content.message)}
                                  </b2x.Div>
                                )}
                                {contentSection.content.cta?.label && (
                                  <b2x.Div>
                                    <b2x.CtaFromContent
                                      {...contentSection.content.cta}
                                      variant={
                                        contentSection.content.cta.variant !== ''
                                          ? contentSection.content.cta.variant
                                          : 'primary'
                                      }
                                    />
                                  </b2x.Div>
                                )}
                              </b2x.Div>
                            </b2x.Div>
                          </b2x.Container>
                        </b2x.Div>
                      )}
                    </b2x.Div>
                  );
                })}
                slidesPerView={1}
              />
              <b2x.Div
                alignItems={'center'}
                className="position-absolute w-100 h-100 top-0 left-0"
                display={{ lg: 'flex', xs: 'none' }}
                justifyContent={'spaceBetween'}
                paddingX={2}
                style={{ pointerEvents: 'none', zIndex: 100 }}
              >
                <Button
                  aria-label={t('accessibility.button.swiper_prev')}
                  //className={slides.length === 1 ? 'd-none' : ''}
                  iconStart={{ name: 'arrow-left', size: 40 }}
                  innerRef={navigationPrevElRef}
                  style={{ pointerEvents: 'initial' }}
                  variant="blank"
                />
                <Button
                  aria-label={t('accessibility.button.swiper_next')}
                  //className={slides.length === 1 ? 'd-none' : ''}
                  iconStart={{ name: 'arrow-right', size: 40 }}
                  innerRef={navigationNextElRef}
                  style={{ pointerEvents: 'initial' }}
                  variant="blank"
                />
              </b2x.Div>
            </b2x.Div>
          )}
        </b2x.SwiperContext>
      )}
    </ComponentWrapper>
  );
};
